import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  AnalyzerPodReport,
  SelectedTicketEntry,
  AnalyzerWeeklyPrompt,
} from '@Models/types';
import { useAuth } from './CognitoAuthProvider';
import { analyzerAPI, analyzerReportsAPI } from '@Lib/axios/analyzer';
import showToast from '@Common/toast/showToast';

interface PodOption {
  id: string;
  name: string;
  reference_num: string;
}

interface AnalyzerReportsContextProps {
  reports: AnalyzerPodReport[];
  setReports: React.Dispatch<React.SetStateAction<AnalyzerPodReport[]>>;
  selectedTickets: Record<string, SelectedTicketEntry>;
  setSelectedTickets: React.Dispatch<
    React.SetStateAction<Record<string, SelectedTicketEntry>>
  >;
  selectedTicket: AnalyzerPodReport['tickets'][0] | null;
  setSelectedTicket: React.Dispatch<
    React.SetStateAction<AnalyzerPodReport['tickets'][0] | null>
  >;
  loading: boolean;
  tagLoading: boolean;
  searchReportDate: string;
  setSearchReportDate: React.Dispatch<React.SetStateAction<string>>;
  searchTicket: string;
  setSearchTicket: React.Dispatch<React.SetStateAction<string>>;
  handleTagTickets: () => Promise<void>;
  handleManualRefetch: () => Promise<void>;
  fetchReports: () => Promise<void>;
  availablePods: PodOption[];
  selectedPod: string;
  setSelectedPod: React.Dispatch<React.SetStateAction<string>>;
  markReportEvaluated: (week: string, podId: string) => void;
  markReportEvaluatedAsync: (
    week: string,
    podId: string,
    evaluated: boolean
  ) => Promise<void>;
  evaluationLoading: Record<string, boolean>;
  // Prompts
  prompts: AnalyzerWeeklyPrompt[];
  setPrompts: React.Dispatch<React.SetStateAction<AnalyzerWeeklyPrompt[]>>;
  promptsLoading: boolean;
  fetchPrompts: () => Promise<void>;
  createPrompt: (
    prompt: Omit<
      AnalyzerWeeklyPrompt,
      '_id' | 'authorEmail' | 'dateCreated' | 'isDefault'
    >
  ) => Promise<void>;
  updatePrompt: (
    originalTitle: string,
    originalAuthor: string,
    prompt: Partial<AnalyzerWeeklyPrompt>
  ) => Promise<void>;
  deletePrompt: (promptTitle: string, authorEmail: string) => Promise<void>;
  setDefaultPrompt: (promptTitle: string, authorEmail: string) => Promise<void>;
  selectedPrompt: AnalyzerWeeklyPrompt | null;
  setSelectedPrompt: React.Dispatch<
    React.SetStateAction<AnalyzerWeeklyPrompt | null>
  >;
}

const AnalyzerReportsContext = createContext<
  AnalyzerReportsContextProps | undefined
>(undefined);

export const AnalyzerReportsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [reports, setReports] = useState<AnalyzerPodReport[]>([]);
  const [selectedTickets, setSelectedTickets] = useState<
    Record<string, SelectedTicketEntry>
  >({});
  const [selectedTicket, setSelectedTicket] = useState<
    AnalyzerPodReport['tickets'][0] | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);
  const [searchReportDate, setSearchReportDate] = useState('');
  const [searchTicket, setSearchTicket] = useState('');
  const [selectedPod, setSelectedPod] = useState<string>('');
  const [cachedReports, setCachedReports] = useState<
    Record<string, AnalyzerPodReport[]>
  >({});
  const [evaluationLoading, setEvaluationLoading] = useState<
    Record<string, boolean>
  >({});
  const [prompts, setPrompts] = useState<AnalyzerWeeklyPrompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [selectedPrompt, setSelectedPrompt] =
    useState<AnalyzerWeeklyPrompt | null>(null);
  const [mappings, setMappings] = useState<PodOption[]>([]);

  const { user, logout } = useAuth();
  const analyzer = useMemo(() => analyzerAPI(logout), [logout]);
  const promptsAnalyzer = useMemo(() => analyzerReportsAPI(logout), [logout]);

  useEffect(() => {
    const fetchMappings = async () => {
      try {
        const podsMap = new Map<string, PodOption>();
        const mappings = await promptsAnalyzer.getMappings();
        mappings.forEach((mapping) => {
          mapping.pods.forEach((pod) => {
            podsMap.set(pod.id, {
              id: pod.id,
              name: pod.name,
              reference_num: pod.reference_num,
            });
          });
        });
        setMappings(Array.from(podsMap.values()));
      } catch (error) {
        console.error('Failed to fetch mappings:', error);
      }
    };

    fetchMappings();
  }, [promptsAnalyzer]);

  const availablePods: PodOption[] = useMemo(() => mappings, [mappings]);

  useEffect(() => {
    if (availablePods.length > 0 && !selectedPod) {
      setSelectedPod(availablePods[0].id);
    }
  }, [availablePods, selectedPod]);

  useEffect(() => {
    if (selectedPod) {
      fetchReports(selectedPod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPod]);

  const fetchReports = async (podId?: string) => {
    const pod = podId ?? selectedPod;
    if (!pod) return;

    const cached = cachedReports[pod];
    if (cached && cached.length > 0) {
      setReports(cached);
      return;
    }

    setLoading(true);
    try {
      const data = await analyzer.getAnalyzerWeeklyReports(pod);
      setReports(data);
      setCachedReports((prev) => ({ ...prev, [pod]: data }));
    } catch (error) {
      showToast.error('Failed to fetch reports:', error as string);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.admin) {
      fetchPrompts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (prompts.length > 0 && !selectedPrompt) {
      const defaultPrompt = prompts.find((p) => p.isDefault);
      setSelectedPrompt(defaultPrompt || prompts[0]);
    }
  }, [prompts, selectedPrompt]);

  const handleTagTickets = async () => {
    const hasSelected = Object.keys(selectedTickets).length > 0;
    if (!hasSelected) {
      showToast.error('No selected tickets to tag.');
      return;
    }
    try {
      setTagLoading(true);
      await analyzer.tagAnalyzerReports(selectedTickets);
      const updatedReports =
        await analyzer.getAnalyzerWeeklyReports(selectedPod);
      setReports(updatedReports);
      setCachedReports((prev) => ({ ...prev, [selectedPod]: updatedReports }));
      showToast.success('Tagging success!');
      setSelectedTickets({});
    } catch (error) {
      showToast.error('Failed to tag selected tickets:', error as string);
    } finally {
      setTagLoading(false);
    }
  };

  const handleManualRefetch = async () => {
    setLoading(true);
    try {
      const refreshed = await analyzer.getAnalyzerWeeklyReports(selectedPod);
      setReports(refreshed);
      setCachedReports((prev) => ({ ...prev, [selectedPod]: refreshed }));
    } catch (error) {
      showToast.error('Failed to fetch reports:', error as string);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrompts = async () => {
    setPromptsLoading(true);
    try {
      const data = await promptsAnalyzer.getPrompts();
      setPrompts(data);
    } catch (error) {
      showToast.error('Failed to fetch prompts:', error as string);
    } finally {
      setPromptsLoading(false);
    }
  };

  const createPrompt = async (
    prompt: Omit<
      AnalyzerWeeklyPrompt,
      '_id' | 'authorEmail' | 'dateCreated' | 'isDefault'
    >
  ) => {
    try {
      await promptsAnalyzer.createPrompt(prompt);
      await fetchPrompts();
      showToast.success('Prompt created successfully!');
    } catch (error) {
      showToast.error('Failed to create prompt:', error as string);
    }
  };

  const updatePrompt = async (
    originalTitle: string,
    originalAuthor: string,
    prompt: Partial<AnalyzerWeeklyPrompt>
  ) => {
    try {
      await promptsAnalyzer.updatePrompt(originalTitle, originalAuthor, prompt);
      await fetchPrompts();
      showToast.success('Prompt updated successfully!');
    } catch (error) {
      showToast.error('Failed to update prompt:', error as string);
    }
  };

  const deletePrompt = async (promptTitle: string, authorEmail: string) => {
    try {
      await promptsAnalyzer.deletePrompt(promptTitle, authorEmail);
      await fetchPrompts();
      showToast.success('Prompt deleted successfully!');
    } catch (error) {
      showToast.error('Failed to delete prompt:', error as string);
    }
  };

  const setDefaultPrompt = async (promptTitle: string, authorEmail: string) => {
    try {
      await promptsAnalyzer.setDefaultPrompt(promptTitle, authorEmail);
      await fetchPrompts();
      showToast.success('Default prompt set successfully!');
    } catch (error) {
      showToast.error('Failed to set default prompt:', error as string);
    }
  };

  const markReportEvaluatedAsync = async (
    week: string,
    podId: string,
    evaluated: boolean
  ) => {
    const key = `${week}_${podId}`;
    setEvaluationLoading((prev) => ({ ...prev, [key]: true }));
    try {
      await analyzer.evaluateReport(week, podId, evaluated);
      const updateReports = (prev: AnalyzerPodReport[]) =>
        prev.map((report) =>
          report.week === week && report.pod.id === podId
            ? {
                ...report,
                evaluated,
                evaluatedAt: evaluated ? new Date().toISOString() : undefined,
              }
            : report
        );

      setReports(updateReports);
      setCachedReports((prevCache) => ({
        ...prevCache,
        [podId]: updateReports(prevCache[podId] || []),
      }));
    } catch (e) {
      // Optionally show toast
    } finally {
      setEvaluationLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const markReportEvaluated = (week: string, podId: string) => {
    const report = reports.find((r) => r.week === week && r.pod.id === podId);
    if (report) {
      markReportEvaluatedAsync(week, podId, !report.evaluated);
    }
  };

  return (
    <AnalyzerReportsContext.Provider
      value={{
        reports,
        setReports,
        selectedTickets,
        setSelectedTickets,
        selectedTicket,
        setSelectedTicket,
        loading,
        tagLoading,
        searchReportDate,
        setSearchReportDate,
        searchTicket,
        setSearchTicket,
        handleTagTickets,
        handleManualRefetch,
        fetchReports,
        availablePods,
        selectedPod,
        setSelectedPod,
        markReportEvaluated,
        markReportEvaluatedAsync,
        evaluationLoading,
        prompts,
        setPrompts,
        promptsLoading,
        fetchPrompts,
        createPrompt,
        updatePrompt,
        deletePrompt,
        setDefaultPrompt,
        selectedPrompt,
        setSelectedPrompt,
      }}
    >
      {children}
    </AnalyzerReportsContext.Provider>
  );
};

export const useAnalyzerReportsContext = (): AnalyzerReportsContextProps => {
  const context = useContext(AnalyzerReportsContext);
  if (!context) {
    throw new Error(
      'useAnalyzerReportsContext must be used within an AnalyzerReportsProvider'
    );
  }
  return context;
};
